import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { Chip } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { format, isValid } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IEsignEmailSendRule } from "../../../../models/esign-email-send-rule";
import MainApi from "../../../../services/mainApi.service";
import { dateOnlyStringToNewDate } from "../../../../utils/conversions";

const translationPath = "4uESign.page.automations.emailSendRules.";
const route = process.env.REACT_APP_4UESIGN + "/api/v1/EsignEmailSendRule/";
const service = new MainApi<IEsignEmailSendRule>(route);

const EmailSendRulesPage = () => {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const formatDate = (value: unknown) => {
    if (typeof value !== "string") return "";

    const date = dateOnlyStringToNewDate(value.substring(0, 10));
    if (!isValid(date)) return "";

    const locale = language === "pt-BR" ? ptBR : language === "es" ? es : enUS;
    return format(date, "P", { locale });
  };

  const columns: GridColDef<IEsignEmailSendRule>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
      flex: 1,
      renderCell: (params) => (
        <OverflowTooltip
          text={params.value}
          tooltip={params.value}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "initialDate",
      headerName: t(translationPath + "initial_date") || "Start date",
      width: 150,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End date",
      width: 150,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t(translationPath + "active")
              : t(translationPath + "inactive")
          }
          color={params.value ? "success" : "error"}
        />
      ),
    },
  ];

  return (
    <Table<IEsignEmailSendRule>
      showDefaultMacroFilters={false}
      service={service}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput
      onAdd={() => navigate("new")}
      onRowClick={(params: GridRowParams<IEsignEmailSendRule>) =>
        navigate(`${params.id.toString()}/edit`)
      }
      shouldShowDeleteButtonForRow={(params) => params.row.enable}
      defaultPageSize={20}
      pageSizeOptions={[5, 10, 20]}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for an email send rule"
      }
      addButtonLabel={t(translationPath + "add") || "Add email send rule"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: { variant: "contained" },
      }}
    />
  );
};

export default EmailSendRulesPage;
